import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Projects from './components/Projects';
import OtherWork from './components/OtherWork';
import Contact from './components/Contact';
const IntuitCaseStudy = React.lazy(() => import('./components/IntuitCaseStudy'));
const MMTCaseStudy = React.lazy(() => import('./components/MMTCaseStudy'));
const WiproCaseStudy = React.lazy(() => import('./components/WiproCaseStudy'));
const RazorpayCaseStudy = React.lazy(() => import('./components/RazorpayCaseStudy'));
const About = React.lazy(() => import('./components/About'));

import FabricCanvas from './components/FabricCanvas';

function MouseCircle() {
  const circleRef = React.useRef(null);
  const mouse = React.useRef({ x: -100, y: -100 });
  const pos = React.useRef({ x: -100, y: -100 });
  const [hovering, setHovering] = useState(false);
  const wasHovering = React.useRef(false);
  const audioCtx = React.useRef(null);
  const clickBuffer = React.useRef(null);

  useEffect(() => {
    // Load audio
    const initAudio = () => {
      if (!audioCtx.current) {
        audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
        fetch(process.env.PUBLIC_URL + '/splat.wav')
          .then(r => r.arrayBuffer())
          .then(buf => audioCtx.current.decodeAudioData(buf))
          .then(decoded => { clickBuffer.current = decoded; })
          .catch(() => {});
      }
      if (audioCtx.current.state === 'suspended') {
        audioCtx.current.resume();
      }
    };
    window.addEventListener('click', initAudio, { once: true });
    window.addEventListener('touchstart', initAudio, { once: true });
    return () => {
      window.removeEventListener('click', initAudio);
      window.removeEventListener('touchstart', initAudio);
    };
  }, []);

  const playClick = React.useCallback(() => {
    if (audioCtx.current && clickBuffer.current) {
      const source = audioCtx.current.createBufferSource();
      source.buffer = clickBuffer.current;
      source.playbackRate.value = 1.8;
      const gain = audioCtx.current.createGain();
      gain.gain.value = 0.3;
      source.connect(gain);
      gain.connect(audioCtx.current.destination);
      source.start(0);
    }
  }, []);

  useEffect(() => {
    wasHovering.current = hovering;
  }, [hovering]);

  useEffect(() => {
    let magnetTarget = null;
    let lastHovering = false;

    const findNearestSnappable = (x, y) => {
      const snappables = document.querySelectorAll('button, .device-btn, .work-tab, .nav-link, .footer-link, .project-pill, .project-arrow, .pills-arrow');
      let nearest = null;
      let nearestDist = Infinity;
      for (const el of snappables) {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
        if (dist < 80 && dist < nearestDist) {
          nearest = { x: cx, y: cy, dist };
          nearestDist = dist;
        }
      }
      return nearest;
    };

    const move = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      const target = e.target;
      const clickable = target.closest('a, button, [role="button"], input, select, textarea, [onclick], .paint-wrapper, h1, h2, h3, .hero-heading, .hero-subheading, .project-image-link, .project-content, .project-arrow, .footer-link, .nav-link, .work-tab, .other-project-card, .frame-mockup, .frame-content, .marquee-img, .marquee-item, img');
      const isHovering = !!clickable;
      setHovering(isHovering);

      if (isHovering && !lastHovering) {
        playClick();
      } else if (!isHovering && lastHovering) {
        playClick();
      }
      lastHovering = isHovering;

      const snap = findNearestSnappable(e.clientX, e.clientY);
      if (snap) {
        const t = 1 - snap.dist / 80;
        magnetTarget = { x: snap.x, y: snap.y, strength: 0.5 * t };
      } else {
        magnetTarget = null;
      }
    };
    window.addEventListener('mousemove', move);

    let raf;
    const animate = () => {
      const targetX = magnetTarget
        ? mouse.current.x + (magnetTarget.x - mouse.current.x) * magnetTarget.strength
        : mouse.current.x;
      const targetY = magnetTarget
        ? mouse.current.y + (magnetTarget.y - mouse.current.y) * magnetTarget.strength
        : mouse.current.y;
      const speed = magnetTarget ? 0.12 : 0.06;
      pos.current.x += (targetX - pos.current.x) * speed;
      pos.current.y += (targetY - pos.current.y) * speed;
      if (circleRef.current) {
        circleRef.current.style.left = `${pos.current.x}px`;
        circleRef.current.style.top = `${pos.current.y}px`;
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', move);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      className={`mouse-circle ${hovering ? 'mouse-circle-hover' : ''}`}
      ref={circleRef}
    />
  );
}

function HomePage() {
  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get('tab') === 'other' ? 'other' : 'casestudies';
  const [activeTab, setActiveTab] = React.useState(initialTab);

  return (
    <>
      <Hero activeTab={activeTab} setActiveTab={setActiveTab} />
      {activeTab === 'casestudies' ? <Projects /> : <OtherWork />}
      <Contact />
    </>
  );
}

export default function App() {
  const location = useLocation();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  React.useEffect(() => {
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      <FabricCanvas />
      <MouseCircle />
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      <Routes>
        <Route path="/" element={<HomePage />} />
            <Route path="/intuit-expert-portal" element={<Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><span
            style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'var(--text-muted)'}}>Loading...</span></div>}><IntuitCaseStudy /></Suspense>} />
                     <Route path="/mmt-live-train-status" element={<Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><span
            style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'var(--text-muted)'}}>Loading...</span></div>}><MMTCaseStudy /></Suspense>} />
                     <Route path="/wipro-deal-pricing-system" element={<Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><span
            style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'var(--text-muted)'}}>Loading...</span></div>}><WiproCaseStudy /></Suspense>} />
                     <Route path="/razorpay-capital" element={<Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><span
            style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'var(--text-muted)'}}>Loading...</span></div>}><RazorpayCaseStudy /></Suspense>} />
                     <Route path="/about" element={<Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><span
            style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'var(--text-muted)'}}>Loading...</span></div>}><About /></Suspense>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
